export type ContactInformationFormValues = {
  name: string;
  email: string;
  phone?: string;
  websiteUrl?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  zipCode?: string;
  state?: string;
  timezone: string;
};

export const CONTACT_INFORMATION_INITIAL_VALUES: ContactInformationFormValues = {
  name: "",
  email: "",
  phone: "",
  websiteUrl: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  zipCode: "",
  state: undefined,
  timezone: "",
};

// Temporary — flip back to false when BE stores timezone.
export const HIDE_PORTAL_ACCESS_FIELDS = true;

// Timezone drops out while the section is hidden — antd's useWatch can't
// see unmounted Form.Items, so leaving it in would deadlock the gate.
export const CONTACT_INFORMATION_REQUIRED_FIELDS: ReadonlyArray<
  keyof ContactInformationFormValues
> = HIDE_PORTAL_ACCESS_FIELDS
  ? ["name", "email"]
  : ["name", "email", "timezone"];

export const getDefaultTimezone = (): string => {
  try {
    // eslint-disable-next-line no-restricted-syntax -- reading the browser IANA tz string, not formatting a date
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  } catch {
    return "UTC";
  }
};

export const isContactInfoComplete = (
  values: Partial<ContactInformationFormValues>,
): boolean =>
  CONTACT_INFORMATION_REQUIRED_FIELDS.every((key) => {
    const v = values[key];
    return typeof v === "string" && v.trim() !== "";
  });

// The user types the full URL — scheme is optional so pasting a bare
// host still validates.
export const WEBSITE_URL_PATTERN =
  /^(?:https?:\/\/)?[\w-]+(?:\.[\w-]+)+(?:\/\S*)?$/i;

export type UsStateOption = {
  value: string;
  label: string;
};

// ISO 3166-2 codes — stable across form, persistence, and reports.
export const US_STATE_OPTIONS: ReadonlyArray<UsStateOption> = [
  { value: "US-AL", label: "Alabama" },
  { value: "US-AK", label: "Alaska" },
  { value: "US-AZ", label: "Arizona" },
  { value: "US-AR", label: "Arkansas" },
  { value: "US-CA", label: "California" },
  { value: "US-CO", label: "Colorado" },
  { value: "US-CT", label: "Connecticut" },
  { value: "US-DE", label: "Delaware" },
  { value: "US-DC", label: "District of Columbia" },
  { value: "US-FL", label: "Florida" },
  { value: "US-GA", label: "Georgia" },
  { value: "US-HI", label: "Hawaii" },
  { value: "US-ID", label: "Idaho" },
  { value: "US-IL", label: "Illinois" },
  { value: "US-IN", label: "Indiana" },
  { value: "US-IA", label: "Iowa" },
  { value: "US-KS", label: "Kansas" },
  { value: "US-KY", label: "Kentucky" },
  { value: "US-LA", label: "Louisiana" },
  { value: "US-ME", label: "Maine" },
  { value: "US-MD", label: "Maryland" },
  { value: "US-MA", label: "Massachusetts" },
  { value: "US-MI", label: "Michigan" },
  { value: "US-MN", label: "Minnesota" },
  { value: "US-MS", label: "Mississippi" },
  { value: "US-MO", label: "Missouri" },
  { value: "US-MT", label: "Montana" },
  { value: "US-NE", label: "Nebraska" },
  { value: "US-NV", label: "Nevada" },
  { value: "US-NH", label: "New Hampshire" },
  { value: "US-NJ", label: "New Jersey" },
  { value: "US-NM", label: "New Mexico" },
  { value: "US-NY", label: "New York" },
  { value: "US-NC", label: "North Carolina" },
  { value: "US-ND", label: "North Dakota" },
  { value: "US-OH", label: "Ohio" },
  { value: "US-OK", label: "Oklahoma" },
  { value: "US-OR", label: "Oregon" },
  { value: "US-PA", label: "Pennsylvania" },
  { value: "US-RI", label: "Rhode Island" },
  { value: "US-SC", label: "South Carolina" },
  { value: "US-SD", label: "South Dakota" },
  { value: "US-TN", label: "Tennessee" },
  { value: "US-TX", label: "Texas" },
  { value: "US-UT", label: "Utah" },
  { value: "US-VT", label: "Vermont" },
  { value: "US-VA", label: "Virginia" },
  { value: "US-WA", label: "Washington" },
  { value: "US-WV", label: "West Virginia" },
  { value: "US-WI", label: "Wisconsin" },
  { value: "US-WY", label: "Wyoming" },
];
